import React, { useMemo } from 'react';

import { FormContainer } from '@eika-kortbetjening/form-container';
import { ErrorMessage, WarningMessage } from '@eika/attention-message';
import { SummaryCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { RichIconTypes } from '@eika/rich-icon';

import ErrorDisplay from './error-display/ErrorDisplay';
import ErrorReceipt from './error-receipt/ErrorReceipt';
import fallbackDictionary from './fallbackDictionary';
import Footer from './footer/Footer';
import SuccessReceipt from './success-receipt/SuccessReceipt';
import { BlockReorderLoader, HardBlockReason, HardBlockReasonType, Piwik, SiteCoreDictionary } from './types';

import './BlockFormConfirmHardBlock.scss';

type ConfirmationTextProps = {
    wishToOrderNewCard: boolean;
    cardShouldBeRenewed: boolean;
    dictionary: SiteCoreDictionary;
    accountName: string;
    formattedAccountNumber: string;
    formattedCardNumber: string;
};

const ConfirmationText = ({
    wishToOrderNewCard,
    cardShouldBeRenewed,
    dictionary,
    accountName,
    formattedAccountNumber,
    formattedCardNumber,
}: ConfirmationTextProps) => {
    const {
        hardBlockConfirmInfoText2Renew,
        hardBlockConfirmInfoText2part1,
        hardBlockConfirmInfoText2part2,
        hardBlockConfirmInfoText2part3,
        hardBlockConfirmInfoNoReorderText1,
        hardBlockConfirmInfoNoReorderText2,
    } = dictionary;
    if (wishToOrderNewCard) {
        if (cardShouldBeRenewed) {
            return (
                <>
                    {hardBlockConfirmInfoText2Renew
                        .replace('{}', `${accountName} (${formattedAccountNumber})`)
                        .replace('{}', `(${formattedCardNumber})`)}
                </>
            );
        }

        return (
            <>
                {hardBlockConfirmInfoText2part1}{' '}
                <strong>
                    {accountName} ({formattedAccountNumber})
                </strong>{' '}
                {hardBlockConfirmInfoText2part2} <strong>({formattedCardNumber})</strong>{' '}
                {hardBlockConfirmInfoText2part3}
            </>
        );
    }

    return (
        <>
            {hardBlockConfirmInfoNoReorderText1}{' '}
            <strong>
                {accountName} ({formattedCardNumber})
            </strong>{' '}
            {hardBlockConfirmInfoNoReorderText2}
        </>
    );
};

type SummaryContentProps = {
    failedHardBlockingCard: boolean;
    wishToChangePin: boolean;
    dictionary: SiteCoreDictionary;
    formattedConfirmBySmsPhoneNumber: string | undefined;
    wishToOrderNewCard: boolean;
    cardShouldBeRenewed: boolean;
    accountName: string;
    formattedAccountNumber: string;
    formattedCardNumber: string;
};

const SummaryContent = ({
    failedHardBlockingCard,
    wishToChangePin,
    dictionary,
    formattedConfirmBySmsPhoneNumber,
    wishToOrderNewCard,
    cardShouldBeRenewed,
    accountName,
    formattedAccountNumber,
    formattedCardNumber,
}: SummaryContentProps) => {
    const {
        hardBlockConfirm,
        confirmBySmsText,
        chosenChangePinText,
        chosenNotChangePinText,
        confirmWarningContactInfo,
        hardBlockConfirmNoNewCardOrder,
        hardBlockRenewalWarning,
        blockError,
    } = dictionary;
    const pinText = wishToChangePin ? chosenChangePinText : chosenNotChangePinText;

    return (
        <div className="summary-content-container">
            <h3 className="h3 hard-block-card-title">
                {wishToOrderNewCard ? hardBlockConfirm : hardBlockConfirmNoNewCardOrder}
            </h3>
            <div className="summary-content-container">
                <SummaryCard icon={RichIconTypes.REGNING_M_KAFFEKOPP} className="hard-block-summary-info-card">
                    <section className="hard-block-summary-content">
                        <p className="confirmation-text">
                            <ConfirmationText
                                wishToOrderNewCard={wishToOrderNewCard}
                                cardShouldBeRenewed={cardShouldBeRenewed}
                                dictionary={dictionary}
                                accountName={accountName}
                                formattedAccountNumber={formattedAccountNumber}
                                formattedCardNumber={formattedCardNumber}
                            />
                        </p>
                        {wishToChangePin && <p className="confirmation-text">{pinText}</p>}
                        {typeof formattedConfirmBySmsPhoneNumber !== 'undefined' ? (
                            <p className="confirmation-text">
                                <span>
                                    {confirmBySmsText}: <strong>{formattedConfirmBySmsPhoneNumber}</strong>.
                                </span>
                            </p>
                        ) : null}
                        {wishToOrderNewCard && <p>{hardBlockRenewalWarning}</p>}
                    </section>
                </SummaryCard>
                {failedHardBlockingCard && <ErrorMessage text={blockError} />}
                {wishToOrderNewCard && !failedHardBlockingCard && (
                    <WarningMessage
                        className="warning-immediate-block"
                        text={confirmWarningContactInfo}
                        icon={IconTypes.INFO_24}
                    />
                )}
            </div>
        </div>
    );
};

type FormContentProps = {
    blockReorderLoader: BlockReorderLoader;
    hardBlockComplete: boolean;
    wishToOrderNewCard: boolean;
    dictionary: SiteCoreDictionary;
    piwik: Piwik | undefined;
    hardBlockReason: HardBlockReasonType;
    wishToChangePin: boolean;
    formattedConfirmBySmsPhoneNumber: string | undefined;
    cardShouldBeRenewed: boolean;
    accountName: string;
    formattedAccountNumber: string;
    formattedCardNumber: string;
};

const FormContent = ({
    blockReorderLoader,
    hardBlockComplete,
    wishToOrderNewCard,
    dictionary,
    piwik,
    hardBlockReason,
    wishToChangePin,
    formattedConfirmBySmsPhoneNumber,
    cardShouldBeRenewed,
    accountName,
    formattedAccountNumber,
    formattedCardNumber,
}: FormContentProps) => {
    const { failedHardBlockingCard, failedReorderingCard } = blockReorderLoader;
    if (failedHardBlockingCard) {
        return <ErrorReceipt dictionary={dictionary} piwik={piwik} />;
    }

    if (hardBlockComplete) {
        return (
            <SuccessReceipt
                blockReorderLoader={blockReorderLoader}
                wishToOrderNewCard={wishToOrderNewCard}
                dictionary={dictionary}
                piwik={piwik}
                showShipmentInfo={wishToOrderNewCard && hardBlockReason === HardBlockReason.LOST}
            />
        );
    }

    if (failedReorderingCard) {
        return (
            <SuccessReceipt
                blockReorderLoader={blockReorderLoader}
                wishToOrderNewCard={wishToOrderNewCard}
                dictionary={dictionary}
                piwik={piwik}
                showShipmentInfo={false}
            />
        );
    }

    return (
        <>
            <SummaryContent
                failedHardBlockingCard={blockReorderLoader.failedHardBlockingCard}
                wishToChangePin={wishToChangePin}
                dictionary={dictionary}
                formattedConfirmBySmsPhoneNumber={formattedConfirmBySmsPhoneNumber}
                wishToOrderNewCard={wishToOrderNewCard}
                cardShouldBeRenewed={cardShouldBeRenewed}
                accountName={accountName}
                formattedAccountNumber={formattedAccountNumber}
                formattedCardNumber={formattedCardNumber}
            />
            <ErrorDisplay blockReorderLoader={blockReorderLoader} dictionary={dictionary} piwik={piwik} />
        </>
    );
};

interface BlockFormConfirmHardBlockProps {
    /** Formatted card number of selected card */
    formattedCardNumber: string;
    /** Formatted account name of selected card */
    accountName: string;
    /** Formatted account number of selected card */
    formattedAccountNumber: string;
    /** The reason for hard block */
    hardBlockReason: HardBlockReasonType;
    /** A flag for if the user wishes to order a new card after hard blocking the selected one */
    wishToOrderNewCard: boolean;
    /** A flag for if the user wishes to set a new pin for a new card after hard blocking and/or replacing the selected one */
    wishToChangePin: boolean;
    /** Function to request a soft block of selected card */
    submit: () => void;
    /** Function for the cancel button */
    cancel: () => void;
    /** Block and reorder loader object */
    blockReorderLoader: BlockReorderLoader;
    /** Phone number to recieve confirmation SMS */
    formattedConfirmBySmsPhoneNumber?: string | undefined;
    /** Sitecore dictionary */
    sitecoreDictionary?: SiteCoreDictionary;
    /** PIWIK tags */
    piwik?: Piwik | undefined;
}

const BlockFormConfirmHardBlock = ({
    formattedCardNumber,
    accountName,
    formattedAccountNumber,
    hardBlockReason,
    wishToOrderNewCard,
    wishToChangePin,
    submit,
    cancel,
    blockReorderLoader,
    sitecoreDictionary,
    piwik,
    formattedConfirmBySmsPhoneNumber,
}: BlockFormConfirmHardBlockProps) => {
    const dictionary: SiteCoreDictionary = { ...fallbackDictionary, ...sitecoreDictionary };

    const {
        isHardBlockingCard,
        failedHardBlockingCard,
        hasHardBlockedCard,
        isReorderingCard,
        hasReorderedCard,
        failedReorderingCard,
    } = blockReorderLoader;

    const cardShouldBeRenewed = useMemo(() => hardBlockReason === HardBlockReason.DAMAGED, [hardBlockReason]);

    const hardBlockComplete = useMemo(() => {
        if (hasHardBlockedCard) {
            if (!wishToOrderNewCard) {
                return true;
            }

            return hasReorderedCard || failedReorderingCard;
        }

        return false;
    }, [hasHardBlockedCard, wishToOrderNewCard, hasReorderedCard, failedReorderingCard]);

    return (
        <FormContainer
            data-track-id={piwik?.blockCardForm?.hardBlockCardConfirmForm?.dataTrackId}
            data-track-linkname={piwik?.blockCardForm?.hardBlockCardConfirmForm?.dataTrackLinkname}
            id={piwik?.blockCardForm?.hardBlockCardConfirmForm?.dataTrackId}
        >
            <FormContainer.Content>
                <FormContent
                    blockReorderLoader={blockReorderLoader}
                    hardBlockComplete={hardBlockComplete}
                    wishToOrderNewCard={wishToOrderNewCard}
                    dictionary={dictionary}
                    piwik={piwik}
                    hardBlockReason={hardBlockReason}
                    wishToChangePin={wishToChangePin}
                    formattedConfirmBySmsPhoneNumber={formattedConfirmBySmsPhoneNumber}
                    cardShouldBeRenewed={cardShouldBeRenewed}
                    accountName={accountName}
                    formattedAccountNumber={formattedAccountNumber}
                    formattedCardNumber={formattedCardNumber}
                />
            </FormContainer.Content>
            <FormContainer.Footer>
                <Footer
                    submit={submit}
                    cancel={cancel}
                    loading={isHardBlockingCard || isReorderingCard}
                    showReceipt={hardBlockComplete}
                    showBlockError={failedHardBlockingCard}
                    cardShouldBeRenewed={cardShouldBeRenewed}
                    dictionary={dictionary}
                    piwik={piwik}
                    wishToOrderNewCard={wishToOrderNewCard}
                />
            </FormContainer.Footer>
        </FormContainer>
    );
};

export default BlockFormConfirmHardBlock;
